
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarIcon, CheckCircle2, ShieldCheck, Rocket, CheckCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Navbar } from "@/components/Navbar";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const getSubscription = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error("Error fetching subscription:", error);
        return;
      }

      setSubscription(data);
    };

    getSubscription();
  }, [user]);

  const handlePlanSelection = (plan, priceId) => {
    setLoading(true);
    
    try {
      // Clear any previous data to avoid conflicts
      localStorage.removeItem('selectedPlan');
      localStorage.removeItem('selectedPriceId');
      
      // Store new selections
      localStorage.setItem('selectedPlan', plan.toLowerCase());
      localStorage.setItem('selectedPriceId', priceId);
      console.log(`Selected plan: ${plan}, priceId: ${priceId}`);
      
      // If premium, go directly to checkout
      if (plan.toLowerCase() === 'premium') {
        navigate('/checkout/redirect');
        toast({
          title: "Premium Plan Selected",
          description: "Redirecting to checkout...",
        });
      } else {
        // For free plan, go to onboarding
        toast({
          title: "Free Plan Selected",
          description: "Let's set up your account!",
        });
        navigate('/onboarding');
      }
    } catch (error) {
      console.error("Error saving plan selection:", error);
      toast({
        title: "Error",
        description: "Failed to save your plan selection. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to check if the user is on this plan
  const isCurrentPlan = (planName) => {
    if (!user || !subscription) return false;
    return subscription.plan === planName.toLowerCase();
  };

  const plans = [
    {
      name: "Free",
      price: "$0",
      description: "Essential wellness tracking & basic personalization",
      features: [
        "AI Assistant: 100 Vita credits/month",
        "Personalized Nutrition Plan",
        "Fitness Routines",
        "Supplement Recommendations",
        "Mindfulness Practices",
        "Progress Tracking"
      ],
      priceId: "free_tier",
      trial: false
    },
    {
      name: "Premium",
      price: "$19.99",
      description: "Advanced wellness features & enhanced personalization",
      features: [
        "AI Assistant: 500 Vita credits/month",
        "Everything in Free",
        "Advanced Wellness Analytics",
        "Customized Fitness Programs",
        "Detailed Supplement Guidance",
        "Expanded Mindfulness Practices",
        "Priority Support"
      ],
      priceId: "price_1R1ajwFapiP8fRW6K74yaoPq",
      trial: false,
      popular: true
    }
  ];

  return (
    <>
      <Navbar />
      <div className="bg-gradient-to-br from-primary/5 to-primary/10 py-12 px-4">
        <div className="container mx-auto">
          <div className="mb-12 text-center">
            <h1 className="text-3xl font-display font-bold tracking-tight mb-3">
              Your Journey to Optimal Wellness Starts Here
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Choose the plan that best fits your wellness goals. Our AI-powered platform provides personalized guidance for nutrition, fitness, supplements, and mindfulness.
            </p>
          </div>

          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto">
            {plans.map((plan, index) => (
              <Card key={index} className={`relative ${plan.popular ? 'border-primary shadow-lg' : ''}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-0 right-0 mx-auto w-fit">
                    <Badge variant="default" className="text-sm">Most Popular</Badge>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl font-semibold">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-4xl font-bold">{plan.price}<span className="text-base font-normal text-muted-foreground">/month</span></div>
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  {isCurrentPlan(plan.name) ? (
                    <Badge variant="outline" className="w-full flex justify-center gap-2 py-2 text-primary">
                      <CheckCircle2 className="h-4 w-4" />
                      Current Plan
                    </Badge>
                  ) : (
                    <Button 
                      className="w-full"
                      onClick={() => handlePlanSelection(plan.name, plan.priceId)}
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "Get Started"}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))}
          </div>

          <div className="mt-12 max-w-2xl mx-auto bg-card p-6 rounded-lg border">
            <h3 className="text-lg font-semibold mb-4">Understanding Vita Credits</h3>
            <p className="text-sm text-muted-foreground mb-4">
              1 Credit = 1 interaction with the AI assistant (e.g., generating or regenerating meal plans, workout routines, supplementation protocols, mindfulness exercises, or asking detailed wellness-related questions).
            </p>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li>• Each tier has a monthly quota clearly stated above.</li>
              <li>• Premium subscribers get priority support and enhanced AI capabilities.</li>
            </ul>
          </div>

          <div className="mt-12 text-center">
            <h2 className="text-2xl font-bold tracking-tight mb-3">
              Why Choose Vitatee?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              Experience a truly personalized approach to wellness, powered by advanced AI technology that adapts to your unique needs and goals.
            </p>

            <div className="grid gap-8 mt-8 grid-cols-1 md:grid-cols-3">
              <div className="text-left">
                <ShieldCheck className="h-6 w-6 text-primary mb-2" />
                <h3 className="text-lg font-semibold">AI-Powered Personalization</h3>
                <p className="text-sm text-muted-foreground">
                  Get tailored recommendations that evolve with your progress and preferences.
                </p>
              </div>

              <div className="text-left">
                <CalendarIcon className="h-6 w-6 text-primary mb-2" />
                <h3 className="text-lg font-semibold">Comprehensive Wellness</h3>
                <p className="text-sm text-muted-foreground">
                  Integrated approach covering nutrition, fitness, supplements, and mindfulness.
                </p>
              </div>

              <div className="text-left">
                <Rocket className="h-6 w-6 text-primary mb-2" />
                <h3 className="text-lg font-semibold">Data-Driven Results</h3>
                <p className="text-sm text-muted-foreground">
                  Track your progress and see real improvements in your wellness journey.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
